import { useGetIdentity, useNotify } from "ra-core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import type { MessageTemplateEdit } from "../../../../supabase/functions/_shared/messageTemplates.ts";

export type MessageTemplateEditRow = MessageTemplateEdit & {
  template_key: string;
  updated_at: string;
};

const QUERY_KEY = ["message-template-edits"];

/** The company's edited templates (one row per edited template). */
export const useMessageTemplateEdits = () => {
  const { identity } = useGetIdentity();
  const query = useQuery({
    queryKey: [...QUERY_KEY, identity?.org_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organization_message_template_edits")
        .select("template_key, subject, body, updated_at");
      if (error) throw error;
      return new Map(
        ((data ?? []) as MessageTemplateEditRow[]).map((row) => [
          row.template_key,
          row,
        ]),
      );
    },
    enabled: identity != null,
    staleTime: 60_000,
  });
  return { edits: query.data ?? new Map(), isPending: query.isPending };
};

/** Save an edit (admins) or delete it to go back to Sigma's default. */
export const useSaveMessageTemplate = () => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  return useMutation({
    mutationFn: async (input: {
      templateKey: string;
      edit: MessageTemplateEdit | null;
    }) => {
      if (!input.edit) {
        const { error } = await supabase
          .from("organization_message_template_edits")
          .delete()
          .eq("template_key", input.templateKey);
        if (error) throw error;
        return;
      }
      const { error } = await supabase
        .from("organization_message_template_edits")
        .upsert(
          {
            template_key: input.templateKey,
            subject: input.edit.subject?.trim() || null,
            body: input.edit.body?.trim() || null,
            updated_by_member_id: identity?.id ?? null,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "org_id,template_key" },
        );
      if (error) throw error;
    },
    onSuccess: (_, input) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      notify(input.edit ? "Template saved" : "Default template restored", {
        type: "success",
      });
    },
    onError: (error: Error) => notify(error.message, { type: "error" }),
  });
};
